import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

// API Service
const API_URL = `${API_BASE_URL}/api`;

const AttendanceService = {
    getAllAttendance: async (companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/attendance`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching all attendance:", error);
            throw error;
        }
    },

    getUserAttendance: async (empNo, companyId, token, userType) => {
        try {
            const response = await axios.get(`${API_URL}/attendance/employee/${empNo}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching user attendance:", error);
            throw error;
        }
    },

    addAttendance: async (attendance, companyId, token, userType) => {
        try {
            const response = await axios.post(`${API_URL}/attendance`, attendance, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error adding attendance:", error);
            throw error;
        }
    },

    deleteAttendance: async (id, companyId, token, userType) => {
        try {
            await axios.delete(`${API_URL}/attendance/${id}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
        } catch (error) {
            console.error("Error deleting attendance:", error);
            throw error;
        }
    },
};

const formatDate = (date) => {
    const options = { year: "numeric", month: "2-digit", day: "2-digit" };
    return new Date(date).toLocaleDateString("en-GB", options);
};

const AttendanceForm = ({ onAttendanceAdded, currentUser, isAdmin, companyId, token, userType }) => {
    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        empNo: "",
        date: "",
        status: "Present",
    });
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEmployees = async () => {
            try {
                const response = await axios.get(`${API_URL}/employees`, {
                    headers: {
                        'user-id': token,
                        'user-type': userType,
                        'company-id': companyId
                    }
                });
                setEmployees(response.data);
            } catch (error) {
                console.error("Error fetching employees:", error);
                setError("Failed to fetch employees. Please try again.");
            }
        };
        
        if (isAdmin) {
            fetchEmployees();
        }
    }, [isAdmin, companyId, token, userType]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const attendanceData = {
                ...form,
                name: employees.find(emp => emp.empNo === form.empNo)?.name,
                submittedAt: new Date().toISOString(),
                submittedBy: currentUser.email
            };
            await AttendanceService.addAttendance(attendanceData, companyId, token, userType);
            setForm({ ...form, date: "", status: "Present" });
            setError(null);
            onAttendanceAdded();
        } catch (error) {
            console.error("Error adding attendance:", error);
            setError("Failed to add attendance record. Please try again.");
        }
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto"
        >
            <h3 className="text-xl font-bold text-gray-700 mb-4">Add Attendance</h3>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}
            
            <select
                name="empNo"
                value={form.empNo}
                onChange={(e) => setForm({ ...form, empNo: e.target.value })}
                required
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value="">Select Employee</option>
                {employees.map((employee) => (
                    <option key={employee.id} value={employee.empNo}>
                        {employee.name} ({employee.empNo})
                    </option>
                ))}
            </select>

            <input
                type="date"
                name="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <select
                name="status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                required
                className="w-full p-3 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
                <option value="Leave">Leave</option>
            </select>

            <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700"
            >
                Add Attendance
            </button>
        </form>
    );
};

const AttendanceList = ({ attendance, onDeleteAttendance, isAdmin }) => {
    return (
        <div className="bg-white p-6 rounded-xl shadow-lg max-w-4xl mx-auto mb-6">
            <h3 className="text-xl font-bold text-gray-700 mb-4">
                {isAdmin ? "All Attendance Records" : "My Attendance Records"}
            </h3>
            <div className="overflow-x-auto">
                <table className="w-full table-auto text-gray-700 rounded-lg">
                    <thead>
                        <tr className="bg-blue-600 text-white">
                            <th className="p-3 text-left">Employee</th>
                            <th className="p-3 text-left">Employee No</th>
                            <th className="p-3 text-left">Date</th>
                            <th className="p-3 text-left">Status</th>
                            {isAdmin && <th className="p-3 text-left">Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {attendance.length > 0 ? (
                            attendance.map((record) => (
                                <tr
                                    key={record.id}
                                    className="odd:bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                                >
                                    <td className="p-3 border-b border-gray-200">{record.name}</td>
                                    <td className="p-3 border-b border-gray-200">{record.empNo}</td>
                                    <td className="p-3 border-b border-gray-200">{formatDate(record.date)}</td>
                                    <td className="p-3 border-b border-gray-200">
                                        <span className={`px-2 py-1 rounded-full text-sm ${
                                            record.status === 'Present' ? 'bg-green-100 text-green-800' :
                                            record.status === 'Absent' ? 'bg-red-100 text-red-800' :
                                            'bg-yellow-100 text-yellow-800'
                                        }`}>
                                            {record.status}
                                        </span>
                                    </td>
                                    {isAdmin && (
                                        <td className="p-3 border-b border-gray-200">
                                            <button
                                                className="text-red-600 font-bold hover:text-red-800"
                                                onClick={() => onDeleteAttendance(record.id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={isAdmin ? 5 : 4} className="p-3 text-center text-gray-500">
                                    No attendance records found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const AttendancePage = () => {
    const [attendance, setAttendance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, userType, companyId, logout } = useAuth();
    const token = localStorage.getItem('token');
    
    const isAdmin = userType === 'user';

    const fetchAttendance = useCallback(async () => {
        if (!companyId) {
            logout();
            return;
        }

        try {
            setLoading(true);
            let data;
            
            if (isAdmin) {
                data = await AttendanceService.getAllAttendance(companyId, token, userType);
            } else if (user) {
                data = await AttendanceService.getUserAttendance(user.empNo, companyId, token, userType);
            }
            
            setAttendance(data || []);
            setError(null);
        } catch (error) {
            console.error("Error fetching attendance:", error);
            setError("Failed to fetch attendance records. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, [user, isAdmin, companyId, token, userType, logout]);

    useEffect(() => {
        if (user && companyId) {
            fetchAttendance();
        } else {
            setLoading(false);
        }
    }, [user, companyId, fetchAttendance]);

    const handleDeleteAttendance = async (id) => {
        try {
            await AttendanceService.deleteAttendance(id, companyId, token, userType);
            setAttendance(attendance.filter(record => record.id !== id));
            setError(null);
        } catch (error) {
            console.error("Error deleting attendance record:", error);
            setError("Failed to delete attendance record. Please try again later.");
        }
    };

    if (loading) {
        return (
            <div className="w-full p-6 flex items-center justify-center bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
                <p className="text-white text-xl">Loading...</p>
            </div>
        );
    }

    return (
        <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
            <h1 className="text-4xl font-bold text-white mb-6 text-center">Attendance Management</h1>
            {user && companyId ? (
                <>
                    {isAdmin && (
                        <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-gray-700 mb-4">Admin Privileges</h3>
                            <p className="text-gray-600">You have admin access to add and manage attendance records for all employees.</p>
                        </div>
                    )}
                    {!isAdmin && (
                        <div className="bg-white p-6 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-gray-700 mb-4">Employee View</h3>
                            <p className="text-gray-600">
                                Welcome, {user.name}. You can view your attendance records below.
                                Contact administration if you need to report attendance issues.
                            </p>
                        </div>
                    )}
                    <AttendanceForm 
                        onAttendanceAdded={fetchAttendance}
                        currentUser={user}
                        isAdmin={isAdmin}
                        companyId={companyId}
                        token={token}
                        userType={userType}
                    />
                    {error && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded max-w-4xl mx-auto">
                            <p>{error}</p>
                        </div>
                    )}
                    <AttendanceList
                        attendance={attendance}
                        onDeleteAttendance={handleDeleteAttendance}
                        isAdmin={isAdmin}
                    />
                </>
            ) : (
                <div className="text-center text-lg text-white">
                    Company information not available. Please login again.
                </div>
            )}
        </div>
    );
};

export default AttendancePage;